import React, { useEffect, useRef, useCallback, useState } from 'react';
import { RealtimeClient } from '@openai/realtime-api-beta';
import { ItemType } from '@openai/realtime-api-beta/dist/lib/client.js';
import { WavRecorder, WavStreamPlayer } from '../lib/wavtools/index.js';
import { ArrowDown, ArrowUp } from 'react-feather';

import './DemoPage.scss';

const LOCAL_RELAY_SERVER_URL: string = process.env.REACT_APP_LOCAL_RELAY_SERVER_URL || '';

interface RealtimeEvent {
  time: string;
  source: 'client' | 'server';
  count?: number;
  event: { [key: string]: any };
}

interface ReferenceProfile {
  userName?: string;
  name?: string;
  role?: string;
  userRole?: string;
  company?: string;
  notes?: { text: string; sentiment: 'positive' | 'negative' | 'neutral' }[];
}

export function DemoPage() {
  const [apiKey, setApiKey] = useState<string | null>(localStorage.getItem('tmp::voice_api_key'));
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<ItemType[]>([]);
  const [realtimeEvents, setRealtimeEvents] = useState<RealtimeEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [expandedEvents, setExpandedEvents] = useState<{ [key: string]: boolean }>({});
  const [isRecording, setIsRecording] = useState(false);
  const startTimeRef = useRef<string>(new Date().toISOString());
  const [referenceProfile, setReferenceProfile] = useState<ReferenceProfile>({});



  const wavRecorderRef = useRef<WavRecorder>(new WavRecorder({ sampleRate: 24000 }));
  const wavStreamPlayerRef = useRef<WavStreamPlayer>(new WavStreamPlayer({ sampleRate: 24000 }));
  const clientRef = useRef<RealtimeClient | null>(null);

  const promptForApiKey = useCallback(() => {
    const enteredApiKey = prompt('Please enter your OpenAI API key:');
    if (enteredApiKey) {
      localStorage.setItem('tmp::voice_api_key', enteredApiKey);
      setApiKey(enteredApiKey);
    } else {
      setError('API key is required to connect.');
    }
  }, []);
  
  useEffect(() => {
    // Check if we need to automatically start the interview
    const shouldAutoStart = localStorage.getItem('autoStartInterview');
    if (shouldAutoStart === 'true') {
      localStorage.removeItem('autoStartInterview');
      startInterview();
    }
  }, []);

  const initializeClient = useCallback(() => {
    if (!apiKey) return;
  
    clientRef.current = new RealtimeClient({
            apiKey,
            dangerouslyAllowAPIKeyInBrowser: true,
            url: LOCAL_RELAY_SERVER_URL,
          }
    );
  
    const client = clientRef.current;

    client.on('connect', () => {
      console.log('Connected to the API');
      setIsConnected(true);
    });

    client.on('disconnect', () => {
      console.log('Disconnected. Attempting to reconnect...');
      setIsConnected(false);
      reconnect();
    });
  
    client.on('error', (error: any) => {
      console.error('Connection error:', error);
      setError('Connection error. Please try reconnecting.');
    });
  }, [apiKey]);
  
  // Reconnect with exponential backoff
  const reconnect = useCallback(() => {
    let attempts = 0;
  
    const attemptReconnect = async () => {
      if (clientRef.current?.isConnected) return;
  
      attempts++;
      const delay = Math.min(1000 * Math.pow(2, attempts), 30000); // Exponential backoff, max delay 30 seconds
      console.log(`Reconnecting in ${delay / 1000} seconds...`);
  
      setTimeout(async () => {
        try {
          if (!clientRef.current) initializeClient();
          await clientRef.current?.connect();
          attempts = 0; // Reset attempts if successful
        } catch (err) {
          console.error('Reconnect attempt failed:', err);
          attemptReconnect(); // Try again
        }
      }, delay);
    };
  
    attemptReconnect();
  }, [initializeClient]);
  
  // Ping to keep the connection alive
  useEffect(() => {
    const client = clientRef.current;
    if (!client) return;
  
    const pingInterval = setInterval(() => {
      if (client.isConnected()) {
        console.log('client is connected');
        client.sendUserMessageContent([{ type: 'input_text', text: '' }]);
      } else {
        console.log('client not connected');
      }
    }, 30000); // Send ping every 30 seconds
  
    return () => {
      clearInterval(pingInterval);
    };
  }, []);

  useEffect(() => {
    // Check for apiKey in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const urlApiKey = urlParams.get('apiKey');
    console.log('url api key:', urlApiKey);

    if (urlApiKey && !localStorage.getItem('tmp::voice_api_key')) {
      // If apiKey is in URL and no key is saved locally, save and use the URL key
      localStorage.setItem('tmp::voice_api_key', urlApiKey);
      setApiKey(urlApiKey);
    } else if (!apiKey) {
      if (localStorage.getItem('tmp::voice_api_key')){
        setApiKey(localStorage.getItem('tmp::voice_api_key'));
      } else {
        console.log('No API key found. Prompting for key...');
        promptForApiKey();
      }

    } 

    // Clean up URL parameter after processing
    if (urlApiKey) {
      urlParams.delete('apiKey');
      const newUrl = `${window.location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [apiKey, promptForApiKey, initializeClient]);


  

  useEffect(() => {
    const wavRecorder = wavRecorderRef.current;
    const checkRecordingStatus = () => {
      setIsRecording(wavRecorder.recording);
    };
    
    // Check status every 100ms
    const intervalId = setInterval(checkRecordingStatus, 100);
    
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const requestMicrophonePermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop()); // Close the stream
      return true; // Permission granted
    } catch (err) {
      console.error('Failed to get microphone permission:', err);
      console.log(String(err));
      if (String(err).includes('NotAllowedError')){
        console.log('true!');
        setError('Microphone access has been denied. Grant microphone access and reload the page and to start the interview.');
      } else {
        setError('Microphone access is required to start the interview.');
      }
      return false; // Permission denied
    }
  }, []);

  useEffect(() => {
    if (realtimeEvents.length > 0 && isLoading) {
      setIsLoading(false);
    }
  }, [realtimeEvents, isLoading]);

  const startInterview = useCallback(async () => {
    if (!apiKey) {
      setError('API key is missing. Please provide a valid API key.');
      return;
    }
    setIsLoading(true);
    const micPermissionGranted = await requestMicrophonePermission();
    if (!micPermissionGranted) {
      setIsLoading(false);
      return; // Exit if permission is not granted
    }

    
  
    try {
      const client = clientRef.current!;
      const wavRecorder = wavRecorderRef.current;
      const wavStreamPlayer = wavStreamPlayerRef.current;
  
      startTimeRef.current = new Date().toISOString();
      setIsConnected(true);
      setRealtimeEvents([]);
      setItems(client.conversation.getItems());
  
      // Begin recording setup
      await wavRecorder.begin();
      await wavStreamPlayer.connect();
  
      await client.connect();
      setIsConnected(true);
      
  
      client.on('connect', async () => {
        console.log('Client on-connect event fired.');
      });
      setTimeout(() => {
      // Set the assistant's instructions and trigger the first response from the assistant
      client.updateSession({
        instructions: `You are an AI assistant conducting a job reference interview. In this interview, you should only ask one simple question at a time and keep the conversation at a brisk pace. 
        Start the conversation by asking the user for their name. Then ask the user to provide the name of a colleague or former colleague they would like to provide a reference for. 
        If either provided name doesn't include a last name, ask for the last name, and repeat back the first and last name combined to confirm you understood correctly and proceed to the next question.
        The other information you want, and should only ask for one at a time with short simple questions, is what company the user and the referenced person worked at together, what role the user and the referenced person had, and the timeline of their working relationship.
        Then, proceed with asking questions about the person's work performance, skills, and character. Ask if there are any specific instances where the person demonstrated strong skills. After the reference has provided 5-6 sufficient answers for creating a reference profile, end by asking them on a scale of 1-10 how strongly they would recommend the person for a similar role,
        then ask if there is anything else they would like to add about the reference. Then when they appear to have completed their response, end the conversation politely.`,
        input_audio_transcription: { model: 'whisper-1' }
      });
  
      // This creates the first response from the assistant automatically
      client.createResponse(); // This line triggers the assistant to start the conversation.
      setTimeout(() => {
        if (client?.conversation?.items?.length === 0) {
          console.log('Race condition detected. Reloading page...');
          localStorage.setItem('autoStartInterview', 'true');
          window.location.reload();
        }
      }, 1000);
      }, 1000);
      console.log('Interview started');
      
    } catch (err) {
      console.error('Failed to start interview:', err);
      setError('Failed to start interview. Please try again.');
    }
  }, [apiKey, requestMicrophonePermission]);
  
  const endInterview = useCallback(async () => {
    const client = clientRef.current!;
    const wavRecorder = wavRecorderRef.current;
    const wavStreamPlayer = wavStreamPlayerRef.current;

    await client.disconnect();
    await wavRecorder.end();
    await wavStreamPlayer.interrupt();

    setIsConnected(false);
    setIsRecording(false);
  }, []);

  const resetAPIKey = useCallback(() => {
    const apiKey = prompt('OpenAI API Key');
    if (apiKey !== null) {
      localStorage.clear();
      localStorage.setItem('tmp::voice_api_key', apiKey);
      window.location.reload();
    }
  }, []);

  const startRecording = async () => {
    setIsRecording(true);
    const client = clientRef.current!;
    const wavRecorder = wavRecorderRef.current;
    try {
      await wavRecorder.record((data) => client.appendInputAudio(data.mono));
    } catch (error) {
      console.error('Failed to start recording:', error);
      setIsRecording(false);
    }
  };
  
  const stopRecording = async () => {
    if (!isRecording) return;
    
    setIsRecording(false);
    const client = clientRef.current!;
    const wavRecorder = wavRecorderRef.current;
    try {
      await wavRecorder.pause();
      client.createResponse();
    } catch (error) {
      console.error('Failed to stop recording:', error);
    }
  };

  useEffect(() => {
    if (apiKey) {
      initializeClient();
    }
  }, [apiKey]);

  useEffect(() => {
    const client = clientRef.current;
    if (!client) return;

    const wavStreamPlayer = wavStreamPlayerRef.current;

    client.on('realtime.event', (realtimeEvent: RealtimeEvent) => {
      setRealtimeEvents((prevEvents) => [...prevEvents, realtimeEvent]);

      // Update reference profile when insights are received
      if (realtimeEvent.event.type === 'insights.updated') {
        console.log('Insights updated:', realtimeEvent.event.insights);
        //const insights = JSON.parse(realtimeEvent.event.insights);
        const profile: ReferenceProfile = {
          userName: '',
          name: '',
          role: '',
          userRole: '',
          company: '',
          notes: []
        };

        // hack workaround for hallucination
        realtimeEvent.event.insights.customFields.forEach((field: { fieldName: string; value: any }) => {
          if (!field){
            console.error('field is null');
            return;
          }
          // if field value is array, set field value to first array item 
          if (Array.isArray(field.value)){
            field.value = field.value[0];
          }
          //console.log('Field:', field.value);
          if (typeof field.value === 'string' && (
            field.value.toLowerCase().startsWith('user') || 
            field.value.toLowerCase().startsWith('assistant') || 
            field.value.toLowerCase().startsWith('un') || 
            field.value.toLowerCase().endsWith(' Doe') || 
            field.value.toLowerCase().includes('not ') || 
            field.value.toLowerCase().includes('colleague') || 
            field.value.toLowerCase().includes('conversation'))) {
            return;
          }
          switch (field.fieldName) {
            case 'userName':
              profile.userName = field.value;
              break;
            case 'referenceName':
              profile.name = field.value;
              break;
            case 'referenceRole':
              profile.role = field.value;
              break;
            case 'userRole':
              profile.userRole = field.value;
              break;
            case 'referenceCompany':
              profile.company = field.value;
              break;
          }
        });

        // also add highlights to the notes 
        if (realtimeEvent.event.insights.highlights){
          profile.notes = realtimeEvent.event.insights.highlights
          .filter((highlight: any) => !highlight?.content?.includes('?'))
          .map((highlight: any) => ({
            text: highlight.content,
            sentiment: highlight?.metadata?.sentiment ?? 'neutral'
          }));
        }

        console.log('profile notes:', profile.notes);

        setReferenceProfile(profile);
      }
    });
      

    client.on('conversation.updated', async ({ item, delta }: any) => {
      const items = client.conversation.getItems();
      if (delta?.audio) {
        wavStreamPlayer.add16BitPCM(delta.audio, item.id);
      }
      if (item.status === 'completed' && item.formatted.audio?.length) {
        const wavFile = await WavRecorder.decode(
          item.formatted.audio,
          24000,
          24000
        );
        item.formatted.file = wavFile;
      }
      setItems(items);
    });

    return () => {
      client.reset();
    };
  }, []);

 
  return (
    <div data-component="ConsolePage" className="flex flex-col p-4 space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <a href="https://www.squiggle.ai">
            <img src="/logo/dark.svg" className="w-20" alt="Logo" />
          </a>
          <h2 className="text-xl font-bold">Squiggle Demo App: Job Reference</h2>
        </div>
        <button className="btn-text"
          onClick={resetAPIKey}
        >{apiKey ? `API Key: ${apiKey?.slice(0, 10)}...` : 'Set API Key'} <span className="icon-[tabler--edit]"></span> </button>
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left Column (Interview/Items) */}
        <div className="flex flex-col space-y-4">
          {!isConnected && (
            <div>
              <p>Click "Start Interview" and approve microphone access to begin providing a job reference.</p>
              {error && <p className="text-red-500">{error}</p>}
            </div>
          )}

          <div className="space-y-2 mt-10">
            {items.map((item) => (
              <div key={item.id} className="p-2 bg-gray-800 rounded-lg text-gray-100">
                <strong>{item.role}</strong>: {item.formatted.text || item.formatted.transcript || '(awaiting response)'}
                {item.formatted.file && (
                  <audio src={item.formatted.file.url} controls className="mt-2 w-full" />
                )}
              </div>
            ))}
          </div>

          {isConnected && (
            <button
              className={`btn-accent btn-soft btn-lg text-xl ${isRecording ? 'bg-red-500' : ''}`}
              onMouseDown={startRecording}
              onMouseUp={stopRecording}
              onMouseLeave={stopRecording}
            >
              {isLoading ? 'Loading...' : isRecording ? 'Release to Send' : 'Push and Hold to Talk'}
            </button>
          )}

          {!isLoading && <button className="btn-accent btn-soft btn-lg"
            onClick={isConnected ? endInterview : startInterview}
          >{isConnected ? 'End Interview' : 'Start Interview'}</button>}
        </div>

        {/* Right Column (Profile and Events/Insights) */}
        <div className="flex flex-col space-y-4">
          {/* Profile Section (Top Right) */}
          <div className="p-4 bg-gray-800 text-white rounded-lg shadow-lg">
            <ReferenceProfile profile={referenceProfile} />
          </div>

          {/* Events & Insights (Bottom Right) */}
          <EventsInsightsTabs 
            realtimeEvents={realtimeEvents}
            expandedEvents={expandedEvents}
            setExpandedEvents={setExpandedEvents}
          />
        </div>
      </div>
    </div>
  );
}
interface EventsInsightsTabsProps {
  realtimeEvents: RealtimeEvent[];
  expandedEvents: { [key: string]: boolean };
  setExpandedEvents: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
}

const EventsInsightsTabs: React.FC<EventsInsightsTabsProps> = ({ realtimeEvents, expandedEvents, setExpandedEvents }) => {
  const [activeTab, setActiveTab] = useState<'events' | 'insights'>('events');

  const filteredEvents = realtimeEvents.filter(event => 
    activeTab === 'events' || event.event.type === 'insights.updated'
  );

  const insights = realtimeEvents.filter(event => event.event.type === 'insights.updated');
  
  const activeTabStyle = 'bg-gray-900 border-gray-700 border-2 text-white text-lg';
  const inactiveTabStyle = 'bg-gray-800 text-gray-400';

  return (
    <div className="flex-1 flex flex-col space-y-4">
      <div className="flex space-x-4">
        <button
          className={`font-bold px-4 py-2 rounded-t-lg ${activeTab === 'events' ? activeTabStyle : inactiveTabStyle}`}
          onClick={() => setActiveTab('events')}
        >
          Events
        </button>
        <button
          className={`font-bold px-4 py-2 rounded-t-lg ${activeTab === 'insights' ? activeTabStyle : inactiveTabStyle}`}
          onClick={() => setActiveTab('insights')}
        >
          Insights {insights.length > 0 && `(${insights.length})`}
        </button>
      </div>
      <div className="overflow-y-scroll border border-gray-300 rounded-lg p-2" style={{maxHeight:360}}>
        {filteredEvents.length === 0 && <p>No {activeTab} yet</p>}
        {filteredEvents.map((event, idx) => (
          <div key={idx} className="cursor-pointer mb-2" onClick={() => {
            const id = `${event.time}-${idx}`;
            setExpandedEvents((prev) => ({
              ...prev,
              [id]: !prev[id],
            }));
          }}>
            <div className={`flex justify-between items-center p-2 rounded-lg ${
              event.event.type === 'insights.updated' ? 'bg-white text-gray-900' : 'bg-gray-900 text-white'
            }`}>
              <div className={event.event.type === 'insights.updated' ? 'text-gray-600' : 'text-gray-400'}>
                {event.source === 'client' ? <ArrowUp className="inline-block"/> : <ArrowDown className="inline-block"/>} {event.source}
              </div>
              <div className="font-semibold">{event.event.type}</div>
            </div>
            {expandedEvents[`${event.time}-${idx}`] && (
              <div className={`mt-2 ml-4 p-2 rounded-lg ${
                event.event.type === 'insights.updated' ? 'bg-gray-100 text-gray-900' : 'bg-gray-800 text-white'
              }`}>
                <pre>{JSON.stringify(event, null, 2)}</pre>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Reference Profile Component
const ReferenceProfile = ({ profile }: { profile: ReferenceProfile }) => {
  return (
    <div>
      <h3 className="text-lg font-bold mb-2">Job Reference Profile</h3>
      <div className="mb-4">
        <p><strong>Reference Name:</strong> {profile?.userName}</p>
        <p><strong>Reference Role:</strong> {profile?.userRole}</p>
        <p className="mt-2"><strong>Job Applicant Name:</strong> {profile?.name}</p>
        <p><strong>Job Applicant Role:</strong> {profile?.role}</p>
        <p><strong>Company:</strong> {profile?.company}</p>

      </div>
      <h4 className="font-semibold mb-2">Notes:</h4>
      <ul className="space-y-2">
        {profile?.notes?.slice(0, 10).map((note, idx) => (
          <li key={idx} className={`p-2 rounded-md ${getNoteStyle(note.sentiment)}`}>
            {note.text}
          </li>
        ))}
      </ul>
    </div>
  );
};

// Helper function for note sentiment styling
const getNoteStyle = (sentiment: string) => {
  switch (sentiment) {
    case 'positive':
      return 'bg-green-700 text-white';
    case 'negative':
      return 'bg-red-700 text-white';
    case 'neutral':
      return 'bg-gray-500 text-white';
    default:
      return 'bg-gray-500 text-white';
  }
};

export default EventsInsightsTabs;